// ─────────────────────────────────────────────────────────────
// src/hooks/useTaxCalculatorStyles.js
// Derives all inline-style objects from theme / screen-size /
// language state so components stay free of style logic.
// ─────────────────────────────────────────────────────────────

const useTaxCalculatorStyles = (theme, currentTheme, screenSize, isRTL, processing) => {
  const { isMobile } = screenSize;

  return {
    container: {
      marginTop: "-40px",
      direction: isRTL ? "rtl" : "ltr",
    },

    card: {
      background:        theme === "dark" ? "rgba(255,255,255,0)" : "#ffffff",
      borderRadius:      "20px",
      padding:           isMobile ? "20px" : "32px",
      marginBottom:      "24px",
      boxShadow:         "none",
      border:            theme === "dark"
        ? "1px solid rgba(255,255,255,0.15)"
        : "1px solid rgba(0,0,0,0.05)",
      transition:        "all 0.3s ease",
      cursor:            "pointer",
      direction:         isRTL ? "rtl" : "ltr",
    },

    title: {
      fontSize:   "27px",
      fontWeight: "700",
      color:      theme === "dark" ? "#fff" : "#1a1a1a",
      textAlign:  isRTL ? "right" : "left",
    },

    subtitle: {
      fontSize:  isMobile ? "14px" : "16px",
      color:     currentTheme.colors.textSecondary,
      textAlign: isRTL ? "right" : "left",
    },

    modeSwitch: {
      display:             "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
      gap:                 "16px",
      marginBottom:        "32px",
    },

    modeSwitchButton: (active) => ({
      padding:    "24px",
      borderRadius: "16px",
      border:     `3px solid ${active ? "#2e7cba" : "transparent"}`,
      background: active
        ? "linear-gradient(135deg, rgba(59,130,246,0.15), rgba(139,92,246,0.15))"
        : currentTheme.colors.inputBackground,
      cursor:    "pointer",
      transition: "all 0.3s ease",
      textAlign: isRTL ? "right" : "left",
    }),

    modeSwitchTitle: {
      fontSize:      isMobile ? "18px" : "20px",
      fontWeight:    "800",
      color:         currentTheme.colors.textPrimary,
      marginBottom:  "8px",
      display:       "flex",
      alignItems:    "center",
      gap:           "10px",
    },

    modeSwitchDesc: {
      fontSize:  "14px",
      color:     currentTheme.colors.textMuted,
      textAlign: isRTL ? "right" : "left",
    },

    calcModeToggle: {
      display:             "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
      gap:                 "16px",
      marginBottom:        "32px",
    },

    calcModeButton: (active) => ({
      padding:     "20px",
      borderRadius: "16px",
      border:      `2px solid ${active ? "#2e7cba" : "transparent"}`,
      background:  active
        ? (theme === "dark" ? "rgba(59,130,246,0.2)" : "rgba(59,130,246,0.1)")
        : currentTheme.colors.inputBackground,
      cursor:     "pointer",
      transition: "all 0.3s ease",
      textAlign:  isRTL ? "right" : "left",
    }),

    calcModeTitle: {
      fontSize:     isMobile ? "16px" : "18px",
      fontWeight:   "700",
      color:        currentTheme.colors.textPrimary,
      marginBottom: "8px",
      display:      "flex",
      alignItems:   "center",
      gap:          "8px",
    },

    calcModeDesc: {
      fontSize:  "13px",
      color:     currentTheme.colors.textMuted,
      textAlign: isRTL ? "right" : "left",
    },

    inputGroup: { marginBottom: "24px" },

    label: {
      display:      "flex",
      alignItems:   "center",
      gap:          "8px",
      fontSize:     "15px",
      fontWeight:   "600",
      color:        currentTheme.colors.textPrimary,
      marginBottom: "12px",
      textAlign:    isRTL ? "right" : "left",
    },

    input: (hasError) => ({
      width:      "100%",
      padding:    "16px 20px",
      borderRadius: "12px",
      border:     hasError
        ? "2px solid #ef4444"
        : `2px solid ${currentTheme.colors.border}`,
      background:  currentTheme.colors.inputBackground,
      color:       currentTheme.colors.textPrimary,
      fontSize:    "16px",
      fontWeight:  "600",
      outline:     "none",
      transition:  "all 0.3s ease",
      boxSizing:   "border-box",
      textAlign:   isRTL ? "right" : "left",
      direction:   isRTL ? "rtl" : "ltr",
    }),

    error: {
      color:     "#ef4444",
      fontSize:  "13px",
      marginTop: "8px",
      display:   "flex",
      alignItems: "center",
      gap:       "6px",
      textAlign: isRTL ? "right" : "left",
    },

    buttonGroup: {
      display:             "grid",
      gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
      gap:                 "12px",
      marginTop:           "32px",
    },

    button: (variant = "primary") => ({
      padding:     "16px 24px",
      borderRadius: "12px",
      border:      "none",
      fontSize:    "16px",
      fontWeight:  "700",
      cursor:      processing ? "not-allowed" : "pointer",
      transition:  "all 0.3s ease",
      display:     "flex",
      alignItems:  "center",
      justifyContent: "center",
      gap:         "8px",
      opacity:     processing ? 0.6 : 1,
      background:  variant === "primary"
        ? "linear-gradient(135deg, #2e7cba, #2e7cba)"
        : variant === "success"
          ? "linear-gradient(135deg, #10b981, #059669)"
          : currentTheme.colors.inputBackground,
      color: variant === "primary" || variant === "success"
        ? "#fff"
        : currentTheme.colors.textPrimary,
    }),

    uploadArea: {
      border:       `2px dashed ${currentTheme.colors.border}`,
      borderRadius: "12px",
      padding:      isMobile ? "32px 16px" : "48px 24px",
      textAlign:    "center",
      cursor:       "pointer",
      transition:   "all 0.3s ease",
      background:   currentTheme.colors.inputBackground,
    },

    uploadIcon: { margin: "0 auto 16px", color: currentTheme.colors.textMuted },

    uploadText: {
      fontSize:     isMobile ? "14px" : "16px",
      color:        currentTheme.colors.textSecondary,
      marginBottom: "8px",
    },

    uploadHint: {
      fontSize: isMobile ? "12px" : "14px",
      color:    currentTheme.colors.textMuted,
    },

    fileName: {
      marginTop:      "12px",
      padding:        "12px",
      background:     "rgba(16,185,129,0.1)",
      borderRadius:   "8px",
      color:          "#10b981",
      fontSize:       isMobile ? "13px" : "14px",
      display:        "flex",
      alignItems:     "center",
      gap:            "8px",
      justifyContent: "center",
    },

    alertBox: (type) => ({
      padding:     isMobile ? "12px" : "16px",
      borderRadius: "8px",
      marginTop:   "16px",
      display:     "flex",
      alignItems:  "center",
      gap:         "12px",
      background:  type === "error" ? "rgba(239,68,68,0.1)" : "rgba(16,185,129,0.1)",
      border:      `1px solid ${type === "error" ? "rgba(239,68,68,0.3)" : "rgba(16,185,129,0.3)"}`,
      color:       type === "error" ? "#ef4444" : "#10b981",
      fontSize:    isMobile ? "13px" : "14px",
      textAlign:   isRTL ? "right" : "left",
    }),

    resultsGrid: {
      display:             "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
      gap:                 "16px",
      marginTop:           "24px",
    },

    resultCard: (color) => ({
      padding:      "20px",
      borderRadius: "16px",
      background:   `linear-gradient(135deg, ${color}10, ${color}05)`,
      border:       `2px solid ${color}30`,
      textAlign:    isRTL ? "right" : "left",
    }),

    resultLabel: {
      fontSize:       "13px",
      fontWeight:     "600",
      color:          currentTheme.colors.textSecondary,
      marginBottom:   "8px",
      textTransform:  "uppercase",
      letterSpacing:  "0.5px",
      textAlign:      isRTL ? "right" : "left",
    },

    resultValue: (color) => ({
      fontSize:   isMobile ? "24px" : "28px",
      fontWeight: "800",
      color,
      display:    "flex",
      alignItems: "center",
      gap:        "8px",
      textAlign:  isRTL ? "right" : "left",
    }),

    summaryCard: {
      padding:      "24px",
      borderRadius: "16px",
      background:   "linear-gradient(135deg, rgba(16,185,129,0.15), rgba(16,185,129,0.05))",
      border:       "2px solid rgba(16,185,129,0.3)",
      marginTop:    "16px",
      direction:    isRTL ? "rtl" : "ltr",
    },

    summaryTitle: {
      fontSize:     "18px",
      fontWeight:   "700",
      color:        "#10b981",
      marginBottom: "16px",
      display:      "flex",
      alignItems:   "center",
      gap:          "8px",
      textAlign:    isRTL ? "right" : "left",
    },

    summaryRow: {
      display:        "flex",
      justifyContent: "space-between",
      alignItems:     "center",
      padding:        "12px 0",
      borderBottom:   currentTheme.colors.borderLight,
    },

    summaryLabel: {
      fontSize:  "14px",
      color:     currentTheme.colors.textSecondary,
      textAlign: isRTL ? "right" : "left",
    },

    summaryValue: {
      fontSize:  "16px",
      fontWeight: "700",
      color:     currentTheme.colors.textPrimary,
      textAlign: isRTL ? "left" : "right",
    },

    table: {
      width:          "100%",
      borderCollapse: "collapse",
      fontSize:       isMobile ? "12px" : "14px",
      minWidth:       "800px",
      direction:      isRTL ? "rtl" : "ltr",
    },

    th: {
      padding:        isMobile ? "10px 12px" : "14px 20px",
      textAlign:      isRTL ? "right" : "left",
      fontWeight:     "700",
      background:     currentTheme.colors.inputBackground,
      color:          currentTheme.colors.textSecondary,
      borderBottom:   `2px solid ${currentTheme.colors.border}`,
      fontSize:       isMobile ? "11px" : "13px",
      letterSpacing:  "0.05em",
      textTransform:  "uppercase",
    },

    td: {
      padding:      isMobile ? "10px 12px" : "12px 20px",
      borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
      color:        currentTheme.colors.textPrimary,
      fontSize:     isMobile ? "11px" : "13px",
      textAlign:    isRTL ? "right" : "left",
    },

    tableWrapper: {
      overflowX:              "auto",
      WebkitOverflowScrolling: "touch",
      marginTop:              "16px",
      direction:              isRTL ? "rtl" : "ltr",
    },
  };
};

export default useTaxCalculatorStyles;